import { NAV_NUMBER } from "../config/config";

/**
 * Utility: Safely parse to number
 */
const toNumber = (val) => {
  const num = Number(val);
  return isNaN(num) ? 0 : num;
};

/**
 * Calculate Portfolio Alerts count based on strategy NAV percentage changes
 */
export function getPortfolioAlertsCount(
  data,
  previousData,
  navNumber = NAV_NUMBER
) {
  if (
    !Array.isArray(data) ||
    !Array.isArray(previousData) ||
    !toNumber(navNumber)
  )
    return 0;

  try {
    // Calculate strategy totals for current data
    const currentStrategyTotals = data.reduce((acc, position) => {
      const strategy = position.StrategyName;
      if (!acc[strategy]) {
        acc[strategy] = {
          name: strategy,
          totalLmv: 0,
          positions: [],
        };
      }
      acc[strategy].totalLmv += Math.max(0, position.market_value || 0);
      acc[strategy].positions.push(position);
      return acc;
    }, {});

    // Add previous day's data to positions
    Object.values(currentStrategyTotals).forEach((strategy) => {
      strategy.positions.forEach((position) => {
        const prevPosition = previousData.find(
          (p) => p.SYMBOL === position.SYMBOL
        );
        position.sodLmv = prevPosition
          ? Math.max(0, prevPosition.market_value || 0)
          : 0;
      });
      strategy.positions.sort((a, b) => (b.LMV || 0) - (a.LMV || 0));
    });

    // Calculate strategy totals for previous data
    const previousStrategyTotals = previousData.reduce((acc, position) => {
      const strategy = position.StrategyName;
      if (!acc[strategy]) {
        acc[strategy] = {
          name: strategy,
          totalLmv: 0,
        };
      }
      acc[strategy].totalLmv += Math.max(0, position.market_value || 0);
      return acc;
    }, {});

    // Find strategies that crossed the threshold (4%)
    const crossed = Object.entries(currentStrategyTotals)
      .filter(([strategy, data]) => {
        let prevLmv = data.positions.reduce(
          (sum, p) => sum + (p.sodLmv || 0),
          0
        );
        const currentLmv = data.totalLmv;
        const prevNavPercent = (prevLmv / NAV_NUMBER) * 100;
        const currentNavPercent = (currentLmv / NAV_NUMBER) * 100;
        return prevNavPercent < 4 && currentNavPercent >= 4;
      })
      .map(([strategy, data]) => ({
        ...data,
        previousLmv: previousStrategyTotals[strategy]?.totalLmv || 0,
      }))
      .sort((a, b) => b.totalLmv - a.totalLmv); // Sort crossed strategies by LMV

    // 8% logic
    const crossed8 = Object.entries(currentStrategyTotals)
      .filter(([strategy, data]) => {
        let prevLmv = data.positions.reduce(
          (sum, p) => sum + (p.sodLmv || 0),
          0
        );
        const currentLmv = data.totalLmv;
        const prevNavPercent = (prevLmv / NAV_NUMBER) * 100;
        const currentNavPercent = (currentLmv / NAV_NUMBER) * 100;
        return prevNavPercent < 8 && currentNavPercent >= 8;
      })
      .map(([strategy, data]) => ({
        ...data,
        previousLmv: previousStrategyTotals[strategy]?.totalLmv || 0,
      }))
      .sort((a, b) => b.totalLmv - a.totalLmv);

    // 15% logic
    const crossed15 = Object.entries(currentStrategyTotals)
      .filter(([strategy, data]) => {
        let prevLmv = data.positions.reduce(
          (sum, p) => sum + (p.sodLmv || 0),
          0
        );
        const currentLmv = data.totalLmv;
        const prevNavPercent = (prevLmv / NAV_NUMBER) * 100;
        const currentNavPercent = (currentLmv / NAV_NUMBER) * 100;
        return prevNavPercent < 15 && currentNavPercent >= 15;
      })
      .map(([strategy, data]) => ({
        ...data,
        previousLmv: previousStrategyTotals[strategy]?.totalLmv || 0,
      }))
      .sort((a, b) => b.totalLmv - a.totalLmv);

    return crossed?.length + crossed8?.length + crossed15?.length || 0;
  } catch (err) {
    console.error("getPortfolioAlertsCount error:", err);
    return 0;
  }
}

/**
 * Calculate metrics for a given strategy
 */
export function calculateStrategyMetrics(data, strategyName) {
  const fallback = {
    totalPnl: 0,
    totalLmv: 0,
    totalSmv: 0,
    buyTradeMV: 0,
    sellTradeMV: 0,
    shortTradeMV: 0,
    coverTradeMV: 0,
    winners: [],
    losers: [],
  };

  if (!Array.isArray(data)) return fallback;

  try {
    const positions = getStrategyPositions(data, strategyName);

    const totals = positions.reduce(
      (sum, p) => {
        sum.pnl += toNumber(p.total_pnl);
        const mv = toNumber(p.market_value);
        sum.lmv += Math.max(0, mv);
        sum.smv += Math.abs(Math.min(0, mv));
        sum.buyTradeMV += toNumber(p.buy_trade_MV);
        sum.sellTradeMV += toNumber(p.sell_trade_MV);
        sum.shortTradeMV += toNumber(p.short_trade_MV);
        sum.coverTradeMV += toNumber(p.cover_trade_MV);
        return sum;
      },
      {
        pnl: 0,
        lmv: 0,
        smv: 0,
        buyTradeMV: 0,
        sellTradeMV: 0,
        shortTradeMV: 0,
        coverTradeMV: 0,
      }
    );

    const strategyGroups = positions.reduce((acc, p) => {
      const strategy = p.StrategyName || "Undefined";
      if (!acc[strategy])
        acc[strategy] = { name: strategy, totalPnl: 0, lmv: 0 };
      acc[strategy].totalPnl += toNumber(p.total_pnl);
      acc[strategy].lmv += Math.max(0, toNumber(p.market_value));
      return acc;
    }, {});

    const sorted = Object.values(strategyGroups).sort(
      (a, b) => b.totalPnl - a.totalPnl
    );

    return {
      totalPnl: totals.pnl,
      totalLmv: totals.lmv,
      totalSmv: totals.smv,
      buyTradeMV: totals.buyTradeMV,
      sellTradeMV: totals.sellTradeMV,
      shortTradeMV: totals.shortTradeMV,
      coverTradeMV: totals.coverTradeMV,
      winners: sorted.slice(0, 5),
      losers: sorted.slice(-5).reverse(),
    };
  } catch (err) {
    console.error("calculateStrategyMetrics error:", err);
    return fallback;
  }
}

/**
 * Filter positions by strategy
 */
function getStrategyPositions(data, strategyName) {
  if (!Array.isArray(data)) return [];

  const creditTypes = [
    "Called or Tendered",
    "Called or Tendered ",
    "Expected Call",
    "RED",
    "Yield to Maturity",
  ];

  const excludedFromRest = [
    "Merger Arbitrage",
    "SPAC",
    "Convertible Arbitrage",
    "Credit Arbitrage",
    ...creditTypes,
  ];

  if (strategyName === "firmwide") return data;

  if (strategyName === "credit") {
    return data.filter((p) => creditTypes.includes(p.StrategyType));
  }

  if (strategyName === "rest") {
    return data.filter((p) => !excludedFromRest.includes(p.StrategyType));
  }

  return data.filter((p) => p.StrategyType === strategyName);
}

/**
 * Calculate NAV %
 */
export const calculateNavPercentage = (value, navNumber = NAV_NUMBER) =>
  toNumber(navNumber) ? (toNumber(value) / navNumber) * 100 : 0;

/**
 * Calculate Basis Points
 */
export const calculateBasisPoints = (value, navNumber = NAV_NUMBER) =>
  toNumber(navNumber) ? (toNumber(value) / navNumber) * 10000 : 0;

/**
 * Format currency
 */
export function formatCurrency(value, showParentheses = true) {
  // if (isNaN(value)) return "-";
  const val = toNumber(value);
  if (val === 0) return "-";
  const formatted = `$${Math.round(Math.abs(val)).toLocaleString()}`;
  return val < 0
    ? showParentheses
      ? `(${formatted})`
      : `-${formatted}`
    : formatted;
}

/**
 * Format plain number
 */
export const formatNumber = (value, decimals = 1) => {
  if (typeof value !== "number" || isNaN(value)) return "-";

  const formatter = new Intl.NumberFormat("en-US", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });

  return value < 0
    ? `(${formatter.format(Math.abs(value))})`
    : formatter.format(value);
};

export const formatPosition = (value, decimals = 0) => {
  if (typeof value !== "number" || isNaN(value)) return "-";

  const formatter = new Intl.NumberFormat("en-US", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });

  return value < 0
    ? `(${formatter.format(Math.abs(value))})`
    : formatter.format(value);
};

/**
 * Format percentage
 */
export const formatPercentage = (value, decimals = 1) =>
  typeof value === "number" && !isNaN(value)
    ? `${value.toFixed(decimals)}%`
    : "-";

/**
 * Format basis points
 */
export const formatBasisPoints = (value, decimals = 1) => {
  const val = toNumber(value);
  const sign = val >= 0 ? "+" : "";
  return `${sign}${val.toFixed(decimals)} bps`;
};

/**
 * Get positions with trades
 */
export const getPositionsWithTrades = (data) => {
  if (!Array.isArray(data)) return [];

  return data.filter(
    (p) =>
      toNumber(p.buy_trade_quantity) ||
      toNumber(p.sell_trade_quantity) ||
      toNumber(p.short_trade_quantity) ||
      toNumber(p.cover_trade_quantity)
  );
};
